/**
 * Бренд-пак на витрине: имя домена и его знак.
 *
 * Имя — ДАННЫЕ, а не перевод: `brand.name` из пака вытесняет одноимённый ключ
 * каталога, потому что имя партнёра правится манифестом, а не вычиткой
 * переводов. Ключ остаётся запасным — на первый заход, пока `/site` не ответил,
 * и на случай, когда пака у домена нет.
 *
 * Но данные знают язык: домен бывает двуязычным, и одно имя на весь домен
 * показывало бы «Румб» кириллицей в английском кабинете. Порядок — язык
 * кабинета (`names[lang]`) → базовое `name` пака → ключ каталога.
 *
 * Знак ВКЛЕИВАЕТСЯ в документ, а не подключается через `<img src>`. Причина —
 * `currentColor`: во внешнем изображении он разрешается против собственного
 * документа SVG, где `color` имеет начальное значение, то есть ЧЁРНЫЙ. Знак на
 * тёмной теме почти исчез бы, а `"mono": true` в манифесте перестал бы что-либо
 * значить. Вклеенный знак наследует `color` кабинета и живёт в обеих темах.
 *
 * Разметка приходит с нашего бэкенда, но это файл ПАРТНЁРА, поэтому в документ
 * уходит не она, а дерево, собранное нами по белому списку (`svgPolicy.ts`):
 * ни `innerHTML`, ни `dangerouslySetInnerHTML` здесь нет вовсе. Guard-тесты
 * стерегут то же самое в CI, но CI смотрит на файл в репозитории, а вклейка
 * происходит в браузере клиента.
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { getLang, subscribeLang, useSiteBrand, useT, type SiteBrand } from '../i18n'
import { isAllowedAttribute, isAllowedTag } from './svgPolicy'

const SVG_NS = 'http://www.w3.org/2000/svg'

/** Потолок размера знака. Логотип — килобайты; мегабайт означает не логотип. */
const MAX_MARK_CHARS = 64 * 1024

/** Потолки обхода: вложенность и число узлов. Знак в них укладывается с запасом. */
const MAX_DEPTH = 24
const MAX_NODES = 2000

/** Что уже приехало по адресу (значение null — приехало и не годится). */
const known = new Map<string, string | null>()
const inFlight = new Map<string, Promise<string | null>>()

/**
 * Имя бренда на заданном языке: пак главнее каталога, каталог — запасной.
 *
 * Чистая функция, а не только хук: выбор языка — это и есть обещание владельцу
 * («rybprognoz.ru остаётся как есть»), и проверять его надо на данных пака, без
 * поднятия React.
 */
export function brandName(brand: SiteBrand | null, lang: string, fallback: string): string {
  return brand?.names[lang] ?? brand?.name ?? fallback
}

/** Имя бренда для текущего языка кабинета. */
export function useBrandName(): string {
  const brand = useSiteBrand()
  const t = useT()
  // Язык берём из store, а не из `useLang()`: имя стоит и там, где провайдера
  // может не быть (граница ошибок, визуальные харнессы) — как и `useSiteBrand`.
  const lang = useSyncExternalStore(subscribeLang, getLang, getLang)
  return brandName(brand, lang, t('brand.name'))
}

/**
 * Разметка знака по адресу пака.
 *
 * Ответ помнится на модуле: знак стоит в рельсе, в шапке и на экране входа
 * одновременно, и без общей памяти каждый из них ходил бы за файлом сам. Адрес
 * уже проверен на «свой origin» при разборе бренда (`i18n/siteCache.ts`).
 */
export function useBrandMark(url: string | undefined): string | null {
  const [markup, setMarkup] = useState<string | null>(() => (url ? known.get(url) ?? null : null))
  useEffect(() => {
    if (!url) {
      setMarkup(null)
      return
    }
    const ready = known.get(url)
    if (ready !== undefined) {
      setMarkup(ready)
      return
    }
    let alive = true
    void loadMark(url).then(loaded => {
      if (alive) setMarkup(loaded)
    })
    return () => {
      alive = false
    }
  }, [url])
  return markup
}

function loadMark(url: string): Promise<string | null> {
  const running = inFlight.get(url)
  if (running) return running
  const started = fetchMark(url)
    .catch(() => null)
    .then(markup => {
      known.set(url, markup)
      inFlight.delete(url)
      return markup
    })
  inFlight.set(url, started)
  return started
}

async function fetchMark(url: string): Promise<string | null> {
  const res = await fetch(url, { credentials: 'same-origin' })
  if (!res.ok) return null
  // Тип ответа проверяем: по этому адресу отвечает наша ручка бренд-ассетов, и
  // всё, что пришло вместо SVG (страница входа прокси, HTML-ошибка), в документ
  // не поедет — разбирать его как знак незачем.
  const kind = res.headers.get('content-type') ?? ''
  if (!kind.toLowerCase().includes('svg')) return null
  const text = await res.text()
  return text.length > MAX_MARK_CHARS ? null : text
}

/**
 * Разметка знака → узел SVG, собранный нами.
 *
 * Доступность решена ЗДЕСЬ, и решение такое: знак остаётся немым. Файл пака
 * несёт `<title>` и `role="img"`/`aria-label` — он ведь отдаётся и по прямой
 * ссылке, где сам себе документ и обязан называться. Но в кабинете рядом со
 * знаком СТОИТ ИМЯ БРЕНДА обычным текстом (рельс, мобильная шапка, экран входа),
 * и вклеенная подпись озвучивалась бы дублем, а на десктопе всплывала бы
 * подсказкой поверх собственного заголовка. Поэтому `<title>`/`<desc>` не входят
 * в белый список элементов, `role`/`aria-label` — в белый список атрибутов, а
 * корню проставляется `aria-hidden`: ровно как у встроенного знака до подмены.
 * Гасит подпись компонент, файл её не теряет.
 */
export function buildMark(markup: string, mono: boolean): SVGSVGElement | null {
  if (typeof DOMParser === 'undefined') return null
  let doc: Document
  try {
    doc = new DOMParser().parseFromString(markup, 'image/svg+xml')
  } catch {
    return null
  }
  // Разбор SVG не бросает исключение, а возвращает документ с `parsererror`.
  if (doc.getElementsByTagName('parsererror').length > 0) return null
  const root = doc.documentElement
  if (!root || root.localName.toLowerCase() !== 'svg') return null

  const budget = { nodes: MAX_NODES }
  const svg = adopt(root, 0, budget) as SVGSVGElement | null
  if (!svg) return null

  // Размер задаёт коробка CSS (className компонента), а не файл партнёра.
  svg.setAttribute('width', '100%')
  svg.setAttribute('height', '100%')
  svg.setAttribute('aria-hidden', 'true')
  svg.setAttribute('focusable', 'false')
  // Моно-знак красится цветом текста; при `mono: false` файл рисуется как есть,
  // своими цветами, и навязывать ему currentColor нельзя — это чужая палитра.
  if (mono) svg.setAttribute('fill', 'currentColor')
  return svg
}

/** Рекурсивная пересборка узла: не клонирование чужого, а сборка своего. */
function adopt(src: Element, depth: number, budget: { nodes: number }): SVGElement | null {
  if (depth > MAX_DEPTH || budget.nodes <= 0) return null
  const tag = src.localName.toLowerCase()
  if (!isAllowedTag(tag)) return null
  budget.nodes -= 1
  const out = document.createElementNS(SVG_NS, tag)
  for (const attr of Array.from(src.attributes)) {
    if (isAllowedAttribute(attr.name, attr.value)) out.setAttribute(attr.name, attr.value)
  }
  // Текстовые узлы не переносятся вовсе: в знаке текста нет, а подпись файла
  // (`<title>`) мы намеренно гасим — см. докстринг buildMark.
  for (const child of Array.from(src.children)) {
    const built = adopt(child, depth + 1, budget)
    if (built) out.appendChild(built)
  }
  return out
}

/**
 * Готовый знак пака как DOM-узел для вставки в свою коробку.
 *
 * Узел строится ПО ЭКЗЕМПЛЯРУ компонента: один и тот же DOM-узел не может
 * стоять в двух местах, а знак рисуется в рельсе, шапке и на входе разом.
 */
export function useMarkElement(
  markup: string | null,
  mono: boolean,
): { svg: SVGSVGElement | null; hostRef: (el: HTMLElement | null) => void } {
  const svg = useMemo(() => (markup ? buildMark(markup, mono) : null), [markup, mono])
  const hostRef = useCallback(
    (el: HTMLElement | null) => {
      if (!el) return
      if (svg) el.replaceChildren(svg)
      else el.replaceChildren()
    },
    [svg],
  )
  return { svg, hostRef }
}
